import { toast } from "react-toastify";
import api from "../../../../../../features/auth/axiosConfig";
import { generatePdf } from "src/business-types/laundry/modules/home/Done/hooks/pdfUtils";
import { buildInvoiceHtml } from "src/business-types/laundry/modules/home/Done/hooks/invoiceTemplate";

export function useInvoiceEmail(defaultOrder?: any) {
  const sendInvoiceEmail = async (email: string, customOrder?: any) => {
    const toastId = toast.loading("Preparing invoice...");

    try {
      if (!email?.trim()) {
        throw new Error("Email is required");
      }

      // Get all orders safely
      const orders = Array.isArray(customOrder?.orders) ? customOrder.orders : customOrder?.orders ? [customOrder.orders] : [];

      // Latest payment/order entry
      const latestOrder = orders.at(-1);

      if (!latestOrder) {
        throw new Error("Order data is missing");
      }

      // Original order with actual total amount
      const baseOrder = orders.find((o: any) => Number(o?.finalAmount) > 0) || latestOrder;

      // Customer details
      const customer = customOrder?.customerDetails;

      const customerName = [customer?.firstName, customer?.middleName, customer?.lastName].filter(Boolean).join(" ");

      // Order ID
      const orderId = latestOrder?.orderId || latestOrder?._id?.$oid || latestOrder?._id || latestOrder?.id || "";

      // Products should come from original order
      const products = baseOrder?.Products || baseOrder?.products || [];

      // Order date
      const orderDate = latestOrder?.orderDate || latestOrder?.Orderdate || latestOrder?.orderedOn?.$date || new Date().toISOString();

      // Safe amount parser
      const parseAmount = (value: any, fieldName: string): number => {
        if (value === undefined || value === null) {
          console.warn(`${fieldName} is undefined/null`);
          return 0;
        }

        const parsed = parseFloat(String(value));

        if (isNaN(parsed)) {
          console.warn(`${fieldName} parsed to NaN from value:`, value);
          return 0;
        }

        return parsed;
      };

      // Total amount should always come from original order
      const totalAmount = parseAmount(baseOrder?.finalAmount, "finalAmount");

      // Payment values should come from latest payment
      const receivedAmount = parseAmount(latestOrder?.receivedAmount ?? latestOrder?.paymentHistory?.[0]?.receivedAmount, "receivedAmount");

      const remainingAmount = parseAmount(latestOrder?.remainingAmount, "remainingAmount");

      // Build invoice HTML
      const htmlContent = buildInvoiceHtml({
        CustomerName: customerName,
        orderDate,
        products,
        totalAmount,
      });

      // Generate PDF
      const pdfBlob = await generatePdf(htmlContent);

      // Prepare form data
      const formData = new FormData();

      formData.append("file", pdfBlob, "Invoice.pdf");
      formData.append("receiverEmail", email.trim());
      formData.append("customerName", customerName);
      formData.append("id", String(orderId));
      formData.append("totalAmount", String(totalAmount));
      formData.append("receivedAmount", String(receivedAmount));
      formData.append("remainingAmount", String(remainingAmount));

      // Send mail
      await api.post(`/app/pos/v2/orders_send_email`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.dismiss(toastId);

      toast.success("Mail sent successfully", {
        autoClose: 1000,
        theme: "colored",
      });
    } catch (error: any) {
      console.error("Error sending invoice email:", error);

      toast.dismiss(toastId);

      const backendMessage = error?.response?.data?.message;

      toast.error(backendMessage || "Failed to send email.", {
        autoClose: 2000,
        theme: "colored",
      });
    }
  };

  return { sendInvoiceEmail };
}
